import { useEffect, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { ArrowUpRight } from "@phosphor-icons/react";

/**
 * Renders ad(s) for a given slot.
 * variant: "banner" | "card" | "compact"
 */
export default function AdSlot({ slot, variant = "card", limit = 1, className = "" }) {
    const [ads, setAds] = useState([]);

    useEffect(() => {
        let alive = true;
        api.get(`/ads?slot=${slot}&limit=${limit}`)
            .then(({ data }) => {
                if (alive) setAds(data || []);
            })
            .catch(() => alive && setAds([]));
        return () => {
            alive = false;
        };
    }, [slot, limit]);

    if (!ads.length) return null;

    const handleClick = (ad) => {
        api.post(`/ads/${ad.id}/click`).catch(() => {});
    };

    const resolveImage = (ad) => ad.image_url || (ad.image_path ? fileUrl(ad.image_path) : null);

    if (variant === "banner") {
        const ad = ads[0];
        const img = resolveImage(ad);
        return (
            <a
                href={ad.link_url}
                target="_blank"
                rel="noopener noreferrer sponsored"
                onClick={() => handleClick(ad)}
                data-testid={`ad-banner-${ad.id}`}
                className={`block group relative border-2 border-black overflow-hidden ${className}`}
            >
                <div className="absolute top-2 left-2 z-10 bg-white text-[10px] font-bold tracking-[0.22em] uppercase px-2 py-0.5 border border-black">
                    Sponsored
                </div>
                <div className="grid md:grid-cols-12">
                    {img && (
                        <div className="md:col-span-5 aspect-[16/9] md:aspect-auto bg-neutral-100 overflow-hidden">
                            <img src={img} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                        </div>
                    )}
                    <div className={`${img ? "md:col-span-7" : "md:col-span-12"} p-6 md:p-8 bg-white flex flex-col justify-center`}>
                        {ad.advertiser_name && (
                            <div className="text-overline text-[#E63946] mb-2">{ad.advertiser_name}</div>
                        )}
                        <h3 className="font-display text-2xl sm:text-3xl font-black tracking-tighter leading-none uppercase mb-2">
                            {ad.title}
                        </h3>
                        {ad.description && (
                            <p className="text-sm text-neutral-600 mb-4 leading-relaxed">{ad.description}</p>
                        )}
                        <span className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-black group-hover:text-[#E63946]">
                            {ad.cta_label || "Visit advertiser"} <ArrowUpRight size={16} weight="bold" />
                        </span>
                    </div>
                </div>
            </a>
        );
    }

    if (variant === "compact") {
        return (
            <div className={`space-y-3 ${className}`}>
                {ads.map((ad) => (
                    <a
                        key={ad.id}
                        href={ad.link_url}
                        target="_blank"
                        rel="noopener noreferrer sponsored"
                        onClick={() => handleClick(ad)}
                        data-testid={`ad-compact-${ad.id}`}
                        className="flex items-center gap-3 border border-black p-3 hover:bg-black hover:text-white transition-colors group"
                    >
                        {resolveImage(ad) && (
                            <img src={resolveImage(ad)} alt="" className="w-14 h-14 object-cover flex-shrink-0" />
                        )}
                        <div className="min-w-0">
                            <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[#E63946]">Sponsored</div>
                            <div className="font-display text-base font-bold leading-tight truncate">{ad.title}</div>
                            <div className="text-xs opacity-70 truncate">{ad.advertiser_name}</div>
                        </div>
                        <ArrowUpRight size={18} weight="bold" className="ml-auto flex-shrink-0 opacity-50 group-hover:opacity-100" />
                    </a>
                ))}
            </div>
        );
    }

    // card grid (default)
    return (
        <div className={`grid sm:grid-cols-${ads.length > 1 ? "2" : "1"} gap-5 ${className}`}>
            {ads.map((ad) => {
                const img = resolveImage(ad);
                return (
                    <a
                        key={ad.id}
                        href={ad.link_url}
                        target="_blank"
                        rel="noopener noreferrer sponsored"
                        onClick={() => handleClick(ad)}
                        data-testid={`ad-card-${ad.id}`}
                        className="group block border border-black bg-white hover:-translate-y-1 transition-transform"
                    >
                        {img && (
                            <div className="aspect-[3/2] bg-neutral-100 overflow-hidden">
                                <img src={img} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                            </div>
                        )}
                        <div className="p-5">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-[10px] font-bold tracking-[0.22em] uppercase text-[#E63946]">Sponsored</span>
                                {ad.advertiser_name && (
                                    <span className="text-[10px] uppercase tracking-widest text-neutral-500 truncate ml-2">
                                        {ad.advertiser_name}
                                    </span>
                                )}
                            </div>
                            <h3 className="font-display text-xl font-bold leading-tight mb-1">{ad.title}</h3>
                            {ad.description && (
                                <p className="text-sm text-neutral-600 leading-relaxed">{ad.description}</p>
                            )}
                            <div className="mt-3 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider group-hover:text-[#E63946]">
                                {ad.cta_label || "Visit advertiser"} <ArrowUpRight size={14} weight="bold" />
                            </div>
                        </div>
                    </a>
                );
            })}
        </div>
    );
}
